import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { FEATURED_LOCATIONS, locationToSlug } from "../lib/locations";
import { BUSINESS } from "../lib/businessInfo";
import { SOCIAL_LINKS } from "../lib/socialLinks";
import BrandMark from "./BrandMark";

const quickLinks = [
  { label: "Home", path: "/" },
  { label: "Teacher Training", path: "/yoga-teacher-training" },
  { label: "Online Courses", path: "/online-courses" },
  { label: "Prenatal Yoga", path: "/prenatal-yoga" },
  { label: "About", path: "/about" },
  { label: "Partnership Agreement", path: "/partnership-agreement" },
  { label: "Contact Us", path: "/contact" },
  { label: "Blog", path: "/blog" },
];

const socials = SOCIAL_LINKS;

function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 group mb-4">
              <BrandMark variant="dark" />
              <span className="font-bold text-white text-lg leading-tight">
                Yoga Retreats <span className="text-emerald-400">Booking</span>
              </span>
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed">
              Find and book authentic yoga retreats, meditation retreats, and yoga teacher
              training programs in Rishikesh, Goa, and across India.
            </p>
            {/* Social links */}
            <div className="flex items-center gap-3 mt-5">
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className="w-9 h-9 rounded-lg bg-gray-800 flex items-center justify-center text-gray-300 hover:bg-emerald-600 hover:text-white transition-colors"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Quick Links
            </h3>
            <ul className="flex flex-col gap-2.5">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Destinations */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Destinations
            </h3>
            <ul className="flex flex-col gap-2.5">
              {FEATURED_LOCATIONS.map((loc) => (
                <li key={loc}>
                  <Link
                    to={`/yoga-retreats-in/${locationToSlug(loc)}`}
                    className="text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    Yoga Retreats in {loc}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Contact
            </h3>
            <ul className="flex flex-col gap-3">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-400">
                  {BUSINESS.contactName}, {BUSINESS.contactTitle}
                  <br />
                  {BUSINESS.operationalAddress.display}
                </span>
              </li>
              <li>
                <a
                  href="tel:+918789758162"
                  className="flex items-center gap-3 text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                >
                  <Phone size={18} className="text-emerald-400 flex-shrink-0" />
                  {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@yogaretreatsbooking.com"
                  className="flex items-center gap-3 text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                >
                  <Mail size={18} className="text-emerald-400 flex-shrink-0" />
                  {BUSINESS.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-center sm:text-left">
            <p className="text-xs text-gray-500" suppressHydrationWarning>
              &copy; {year} {BUSINESS.tradeName}. All rights reserved.
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {BUSINESS.udyamLabel} &middot; {BUSINESS.udyamRegistration}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
            <Link
              to="/partnership-agreement"
              className="text-xs text-gray-500 hover:text-emerald-400 transition-colors"
            >
              Partnership Agreement
            </Link>
            {socials.map(({ label, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:text-emerald-400 transition-colors"
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
